import React, { useState, useRef, useEffect } from 'react';
import { accountPasswordResetConfirm, errText } from '../services/api';
import { isNetworkError } from '../services/offlineQueue';
import { readLinkToken, scrubLinkToken } from '../utils/accountLinkRoute';
import AccountForgotPassword, { RESET_LINK_TTL_MINUTES } from './AccountForgotPassword';
import { T, S } from '../styles/ds';

// The minimum is duplicated from the backend: `settings.MIN_ACCOUNT_PASSWORD_LENGTH`
// (backend/app/config.py) is the source of truth and no config endpoint exposes it to the frontend
// today, so these two numbers must be changed together. The maximum is bcrypt's own 72-byte
// truncation limit, hard-coded in `_validate_account_password` for the same reason.
const MIN_PASSWORD_BYTES = 8;
const MAX_PASSWORD_BYTES = 72;

// UTF-8 BYTES, not characters. This is the exact bug `_validate_account_password`
// (backend/app/models/account.py:51) exists to prevent: a Devanagari letter is three bytes and an
// emoji four, so "आठअक्षर" passes a `.length >= 8` check while a 30-character Devanagari password
// sails past `.length <= 72` and then blows up bcrypt server-side. Counting characters here would
// just move that failure to the server and show the owner a raw Pydantic string.
const byteLen = (pw) => new TextEncoder().encode(pw || '').byteLength;

// One Hindi letter or emoji counting for more than one is not something an owner can be expected to
// guess, so every message about length says it.
const MULTIBYTE_NOTE = 'हिंदी का एक अक्षर या इमोजी एक से ज़्यादा गिना जाता है';
const MULTIBYTE_NOTE_EN = 'one Hindi letter or emoji counts as more than one';

// The server stays the authority on the password rules, but its 422 is an array whose text —
// "Value error, Password must be 8-72 bytes (UTF-8)" — is English-only and technical, and errText()
// would render it verbatim. Map it instead; never echo it raw.
const PASSWORD_REJECTED_MSG = `यह पासवर्ड इस्तेमाल नहीं हो सकता — कम से कम ${MIN_PASSWORD_BYTES} और ज़्यादा से ज़्यादा ${MAX_PASSWORD_BYTES} (${MULTIBYTE_NOTE}) / This password cannot be used — at least ${MIN_PASSWORD_BYTES} and at most ${MAX_PASSWORD_BYTES} (${MULTIBYTE_NOTE_EN})`;
const NET_ERROR_MSG = 'सर्वर से संपर्क नहीं हो सका — पासवर्ड नहीं बदला। इंटरनेट जाँचें और फिर कोशिश करें / Could not reach the server — the password was not changed. Check the internet and try again';
const GENERIC_ERROR_MSG = 'पासवर्ड नहीं बदला जा सका — फिर कोशिश करें / Could not change the password — please try again';
// Shown when the link itself is no longer usable. The backend's two sentences ("Invalid or expired
// reset link" / "This reset link has already been used") both resolve to one outcome — this link no
// longer works, ask for a new one — so this screen never explains WHICH cause fired and never
// mentions epochs, scopes or token mechanics. Passing the backend's own sentence through is fine;
// inventing a sharper diagnosis is not.
const DEAD_LINK_MSG = 'यह लिंक अब काम नहीं करता — नया लिंक माँगें / This link no longer works — please ask for a new one';
const INCOMPLETE_LINK_MSG = 'लिंक अधूरा है / This link is incomplete — open the link from your email again';

// Screen 2 of the Owner Account password reset (PH-68): set a new password from the emailed link.
//
// `onSignIn` takes the owner to the Owner Account sign-in screen. It is the single way forward from
// every terminal state here — success, a dead link, and the request-a-new-link screen — so no state
// on this screen is ever a dead end.
function AccountResetPassword({ onSignIn }) {
  // Captured ONCE, in a pure initializer with no side effect of any kind. The URL scrub lives in the
  // effect below, deliberately: index.js renders under React.StrictMode, which double-invokes the
  // render body, so scrubbing here would let the second pass read an already-stripped URL and
  // capture an empty token — reporting "this link is incomplete" for a link that was perfectly
  // good. SetupWizard.jsx:13-22 has exactly that side effect inside its initializer; this mirrors
  // its intent, not its shape.
  const [token] = useState(() => readLinkToken());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [deadLink, setDeadLink] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  // Whitespace-only counts as absent: it can never be a real token, and treating it as one would
  // send a request that is certain to 401.
  const hasToken = !!token.trim();

  // Set true on mount and false ONLY in the cleanup — PH-62's regression was a ref that only ever
  // went false, which StrictMode's double-invoke (setup, cleanup, setup again) then left stuck
  // false, silently no-op-ing every submit on the first real mount.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Belt and braces for “a double-click sends exactly one request”, matching
  // AccountForgotPassword.jsx's own guard: two clicks in the same tick both read the
  // pre-update `loading` value, so only a synchronously-updated ref can refuse the second one
  // before it reaches the network.
  const inFlightRef = useRef(false);

  // Take the token out of the address bar, so it never reaches history, a bookmark, a shared URL or
  // a screenshot (AGENTS.md §6). In an EFFECT, never the initializer above; replaceState is
  // idempotent, so StrictMode running this twice is harmless.
  //
  // This makes a refresh tokenless, which is safe precisely because VIEWING this page consumes
  // nothing: only a successful POST blocklists the token's `jti`, and nothing bumps the account's
  // reset epoch on a mere view — so the same emailed link still works for its full 30 minutes. The
  // missing-token state below is what names the way out after a refresh.
  useEffect(() => { scrubLinkToken(); }, []);

  const validatePasswords = () => {
    const errs = {};
    const bytes = byteLen(password);
    if (!password) {
      errs.password = 'नया पासवर्ड डालें / Enter a new password';
    } else if (bytes < MIN_PASSWORD_BYTES) {
      errs.password = `पासवर्ड बहुत छोटा है — कम से कम ${MIN_PASSWORD_BYTES} चाहिए (${MULTIBYTE_NOTE}) / Password is too short — at least ${MIN_PASSWORD_BYTES} needed (${MULTIBYTE_NOTE_EN})`;
    } else if (bytes > MAX_PASSWORD_BYTES) {
      errs.password = `पासवर्ड बहुत लंबा है — ${MAX_PASSWORD_BYTES} से ज़्यादा नहीं (${MULTIBYTE_NOTE}) / Password is too long — no more than ${MAX_PASSWORD_BYTES} (${MULTIBYTE_NOTE_EN})`;
    }
    if (!confirmPassword) {
      errs.confirmPassword = 'पासवर्ड दोबारा डालें / Re-enter the password';
    } else if (password && confirmPassword !== password) {
      errs.confirmPassword = 'दोनों पासवर्ड एक जैसे नहीं हैं / The two passwords do not match';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (inFlightRef.current || loading || done || !hasToken) return;
    const errs = validatePasswords();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;   // nothing is sent
    inFlightRef.current = true;
    setLoading(true);
    setApiError('');
    try {
      await accountPasswordResetConfirm(token, password);
      if (!mountedRef.current) return;
      // Nothing is written to localStorage here, and no session exists to write: the backend has
      // just stamped `sessions_reset_at`, which invalidates every live session this account had.
      setDone(true);
      setPassword('');
      setConfirmPassword('');
    } catch (err) {
      if (!mountedRef.current) return;
      const status = err?.response?.status;
      if (isNetworkError(err)) {
        // Keep the typed password: nothing reached the server, so it is still worth having and the
        // owner should not have to type it twice. This mirrors AccountSignIn.jsx's own split
        // between a real rejection and a connection problem.
        setApiError(NET_ERROR_MSG);
      } else if (status === 401) {
        // Both 401 reasons land here and resolve to the same outcome. The backend's own sentence is
        // passed through when there is one; the fallback never sharpens it.
        setDeadLink(errText(err, DEAD_LINK_MSG));
        setPassword('');
        setConfirmPassword('');
      } else if (status === 422) {
        setApiError(PASSWORD_REJECTED_MSG);
        setPassword('');
        setConfirmPassword('');
      } else {
        // Deliberately no 429 branch: `password_reset_confirm` calls neither `check_ip_throttle` nor
        // `record_ip_failure`, so handling 429 here would be handling a status this endpoint cannot
        // return.
        setApiError(errText(err, GENERIC_ERROR_MSG));
        setPassword('');
        setConfirmPassword('');
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  };

  // Same page-wrapper shape as AccountSignIn.jsx — design-system tokens only.
  const wrapStyle = { minHeight: '100vh', background: T.bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: T.font };
  const btnDisabled = { opacity: 0.5, cursor: 'default' };
  const fieldErrStyle = { ...S.alertDetail, marginTop: -6, marginBottom: 10 };

  // Asking for a fresh link is the one way forward from every dead-link state, so it reuses Screen 1
  // rather than duplicating it. Cancelling from there goes to sign-in, not back to a link that is
  // already known to be dead.
  if (requesting) {
    return <AccountForgotPassword onCancel={onSignIn} />;
  }

  const askForNewLinkButton = (
    <button
      type="button" onClick={() => setRequesting(true)}
      style={{ ...S.btnPri, width: '100%', marginTop: 12 }}
    >
      नया लिंक माँगें / Ask for a new link →
    </button>
  );

  return (
    <div style={wrapStyle}>
      <div style={{ marginBottom: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: T.ink, letterSpacing: -0.5 }}>नया पासवर्ड बनाएँ</div>
        <div style={{ fontSize: 13, color: T.sec, marginTop: 4 }}>Set a new account password</div>
      </div>

      <div style={{ ...S.cardPad, width: '100%', maxWidth: 360 }}>
        {done ? (
          <div>
            <div
              role="status"
              style={{
                padding: '14px 16px', background: T.green.bg, border: `1px solid ${T.green.border}`,
                borderRadius: T.rs, fontFamily: T.font, textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 6 }} aria-hidden="true">✅</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.green.text, marginBottom: 6 }}>
                पासवर्ड बदल दिया गया है / Your password has been changed
              </div>
              <div style={{ fontSize: 12, color: T.sec }}>
                सुरक्षा के लिए बाकी सभी डिवाइस से साइन आउट कर दिया गया है — अब नए पासवर्ड से साइन इन करें / For safety, every other signed-in device has been signed out — sign in now with the new password
              </div>
            </div>
            <button
              type="button" onClick={onSignIn}
              style={{ ...S.btnPri, width: '100%', marginTop: 12 }}
            >
              साइन इन करें / Sign in →
            </button>
          </div>
        ) : deadLink ? (
          <div>
            <div role="alert" style={S.alertBox}>
              <div style={S.alertTitle}>{DEAD_LINK_MSG}</div>
              <div style={{ ...S.alertDetail, marginBottom: 0 }}>{deadLink}</div>
            </div>
            {askForNewLinkButton}
          </div>
        ) : !hasToken ? (
          // A refresh lands here by design — the effect above scrubbed the token out of the URL.
          // Nothing is sent to the server in this state, and the emailed link is still good, so the
          // copy points back at the email first and offers a fresh link as the second way out.
          <div>
            <div role="alert" style={S.alertBox}>
              <div style={S.alertTitle}>{INCOMPLETE_LINK_MSG}</div>
              <div style={{ ...S.alertDetail, marginBottom: 0 }}>
                ईमेल में आया पूरा लिंक दोबारा खोलें — वह {RESET_LINK_TTL_MINUTES} मिनट तक काम करता है / Open the full link from your email again — it works for {RESET_LINK_TTL_MINUTES} minutes
              </div>
            </div>
            {askForNewLinkButton}
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ fontSize: 12, color: T.sec, marginBottom: 12 }}>
              नया पासवर्ड चुनें — कम से कम {MIN_PASSWORD_BYTES} बाइट्स ({MULTIBYTE_NOTE}) / Choose a new password — at least {MIN_PASSWORD_BYTES} bytes ({MULTIBYTE_NOTE_EN})
            </div>
            <label htmlFor="reset-new-password" style={S.label}>नया पासवर्ड / New password</label>
            <input
              id="reset-new-password" type="password" autoComplete="new-password" autoFocus required
              value={password}
              onChange={e => { setPassword(e.target.value); setFieldErrors({}); setApiError(''); }}
              placeholder="••••••••" style={{ ...S.input, marginBottom: 12 }}
            />
            {fieldErrors.password && <div role="alert" style={fieldErrStyle}>{fieldErrors.password}</div>}

            <label htmlFor="reset-confirm-password" style={S.label}>पासवर्ड दोबारा / Confirm password</label>
            <input
              id="reset-confirm-password" type="password" autoComplete="new-password" required
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setFieldErrors({}); setApiError(''); }}
              placeholder="••••••••" style={{ ...S.input, marginBottom: 12 }}
            />
            {fieldErrors.confirmPassword && <div role="alert" style={fieldErrStyle}>{fieldErrors.confirmPassword}</div>}

            {apiError && <div role="alert" style={{ ...S.alertDetail, textAlign: 'center' }}>{apiError}</div>}

            <button
              type="submit" disabled={loading}
              style={{ ...S.btnPri, width: '100%', ...(loading ? btnDisabled : {}) }}
            >
              {loading ? 'बदला जा रहा है...' : 'पासवर्ड बदलें / Change password →'}
            </button>
          </form>
        )}

        {!done && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <button
              type="button" onClick={onSignIn}
              style={{ ...S.btnSec, background: 'none', border: 'none', color: T.mute, fontSize: 12, cursor: 'pointer' }}
            >
              ← साइन इन पर वापस जाएँ / Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccountResetPassword;
